import {ref, computed, watch, watchEffect, onMounted, onUnmounted, nextTick} from '@vue/composition-api'
import store from '@/store'
import i18n from '@/libs/i18n'
import assetStoreModule from '@/views/main/asset/assetStoreModule'
import fenceStoreModule from '@/views/main/fence/fenceStoreModule'
import { resolveIcon, toAbsoluteIconUrl } from '@/utils/assetUtils'
import axios from '@/libs/axios'
import {latLng} from 'leaflet'

const ASSET_APP_STORE_MODULE_NAME = 'app-asset'
const FENCE_APP_STORE_MODULE_NAME = 'app-fence'
const VIEWPORT_THRESHOLD = 100 // Limite para usar fetchAssetViewPort (2 em teste, 100 em produção)
const DEBOUNCE_DELAY = 600 // Debounce pan/zoom — reduz rajadas de fetchAssetViewPort
/** Lotes paralelos ao buscar geometria completa de cada cerca (antes: 1 request por vez em série). */
const FENCE_DETAIL_BATCH_SIZE = 8
/** Debounce ao mudar filtros — evita refetch duplo e agrupa alterações rápidas. */
const FILTER_WATCH_DEBOUNCE_MS = 350
/** Mínimo entre buscas summary+asset por TLM (severe/failure) para o mesmo ativo. */
const TLM_DISPLAY_MIN_INTERVAL_MS = 15000

const t = (key) => (i18n.t ? i18n.t(key) : key)

const resolveBaseLayer = (name) => {
    if (!name) return null
    if (name === 'map' || name === t('Map')) return 'map'
    if (name === 'satellite' || name === t('Satellite')) return 'satellite'
    const lower = String(name).toLowerCase()
    if (lower === 'map' || lower.endsWith('apa')) return 'map'
    if (lower.startsWith('sat')) return 'satellite'
    return null
}

const isLabelsOverlay = (name) => (
    name === t('Show locations')
)

const isRoadsOverlay = (name) => (
    name === t('Show street names')
)

export default function useMap() {
    // Registrar módulo do Vuex se não existir
    if (!store.hasModule(ASSET_APP_STORE_MODULE_NAME)) {
        store.registerModule(ASSET_APP_STORE_MODULE_NAME, assetStoreModule)
    }
    if (!store.hasModule(FENCE_APP_STORE_MODULE_NAME)) {
        store.registerModule(FENCE_APP_STORE_MODULE_NAME, fenceStoreModule)
    }

    // Referência ao mapa
    const mapRef = ref(null)
    const mapInstance = ref(null)

    // Estado do mapa
    const mapReady = ref(false)
    const center = ref([-19.801187, -43.948885]) // Centro padrão (Brasil)
    const zoom = ref(5)
    const mapOptions = ref({
        scrollWheelZoom: true,
        doubleClickZoom: true,
        boxZoom: true,
        keyboard: true,
    })

    /** Prefixo localStorage para preferências do NewMap (evita conflito com Map.vue). */
    const STORAGE_KEY_PREFIX = 'newMap_'

    // Estado dos ativos e clusters
    const assets = ref([])
    const clusters = ref([])
    const loading = ref(false)
    const error = ref(null)
    const totalAssets = ref(0)
    const useViewPortMode = ref(false)
    const viewPortInitialLoadDone = ref(false)
    /** Evita rajadas de GET summary+asset no mesmo ativo por eventos TLM (severe/failure). */
    const lastTlmDisplayFetchAt = ref({})

    // Estado das cercas virtuais (carregamento sob demanda — ver showFencesOnMap)
    const circleFences = ref([])
    const polygonFences = ref([])
    const fencesLoading = ref(false)
    /** Preferência persistida; padrão false para melhor performance no primeiro carregamento. */
    const showFencesOnMap = ref(
        typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY_PREFIX + 'showFences') !== null
            ? localStorage.getItem(STORAGE_KEY_PREFIX + 'showFences') === 'true'
            : false
    )
    /** Invalida respostas antigas se o usuário desligar cercas ou disparar novo carregamento. */
    let fenceLoadSeq = 0

    // Filtros
    const customerId = ref('')
    const assetTypeId = ref('')
    const assetBrandId = ref('')
    const assetModelId = ref('')
    const search = ref('')

    // Opções dos filtros
    const manufacturerOptions = ref([])
    const assetTypeOptions = ref([])
    const modelOptions = ref([])
    const customerOptions = ref([])

    // Estado dos filtros
    const showFilters = ref(false)
    const searchOwner = ref('')
    const filteredCustomerOptions = ref([])
    const isFittingBounds = ref(false)

    // Modo de visualização: 'icon' ou 'point' — padrão 'icon', persistido no localStorage
    const viewMode = ref(
        typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY_PREFIX + 'viewMode')
            ? localStorage.getItem(STORAGE_KEY_PREFIX + 'viewMode')
            : 'icon'
    )

    // URLs das camadas do mapa
    const arcgisSatelliteUrl = 'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
    const arcgisLabelsUrl = 'https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
    const arcgisRoadsUrl = 'https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}'

    // Camada base: 'map' ou 'satellite' — padrão 'satellite', apenas localidades (sem ruas)
    const defaultLayer = ref(
        typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY_PREFIX + 'defaultLayer')
            ? localStorage.getItem(STORAGE_KEY_PREFIX + 'defaultLayer')
            : 'satellite'
    )

    // Overlay "Mostrar Localidades" — padrão true
    const showLocalities = ref(
        typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY_PREFIX + 'showLocalities') !== null
            ? localStorage.getItem(STORAGE_KEY_PREFIX + 'showLocalities') === 'true'
            : true
    )

    // Overlay "Mostrar nome das Ruas" — padrão false (apenas localidades, sem ruas)
    const showRoads = ref(
        typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY_PREFIX + 'showRoads') !== null
            ? localStorage.getItem(STORAGE_KEY_PREFIX + 'showRoads') === 'true'
            : false
    )

    const hasActiveFilters = computed(() => {
        return !!(
            customerId.value ||
            assetTypeId.value ||
            assetBrandId.value ||
            assetModelId.value ||
            searchOwner.value ||
            search.value
        )
    })

    const assetsWithValidCoordinates = computed(() => {
        const validAssets = assets.value.filter(asset => {
            const lat = asset.lastLat
            const lng = asset.lastLng
            return lat != null && lng != null && lat !== 0 && lng !== 0
        })

        return validAssets.sort((a, b) => {
            const statusA = (a.lastWorkstatus || '').toLowerCase()
            const statusB = (b.lastWorkstatus || '').toLowerCase()

            const isPriorityA = statusA === 'severe' || statusA === 'failure'
            const isPriorityB = statusB === 'severe' || statusB === 'failure'

            if (isPriorityA && !isPriorityB) return 1
            if (isPriorityB && !isPriorityA) return -1
            return 0
        })
    })

    // Persistir preferências no localStorage quando alteradas
    watch(defaultLayer, (val) => {
        if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY_PREFIX + 'defaultLayer', val)
    }, { immediate: false })
    watch(showLocalities, (val) => {
        if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY_PREFIX + 'showLocalities', String(val))
    }, { immediate: false })
    watch(showRoads, (val) => {
        if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY_PREFIX + 'showRoads', String(val))
    }, { immediate: false })
    watch(viewMode, (val) => {
        if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY_PREFIX + 'viewMode', val)
    }, { immediate: false })

    /** Debounce com timer por função (o timer compartilhado anterior cancelava pan e filtros entre si). */
    const debounce = (func, delay) => {
        let timer = null
        const wrapped = (...args) => {
            if (timer) clearTimeout(timer)
            timer = setTimeout(() => {
                timer = null
                func(...args)
            }, delay)
        }
        wrapped.cancel = () => {
            if (timer) {
                clearTimeout(timer)
                timer = null
            }
        }
        return wrapped
    }

    // Padrões de URL para identificar camadas (vue2-leaflet adiciona todas ao mapa na montagem)
    const URL_OSM = 'openstreetmap.org'
    const URL_SATELLITE = 'World_Imagery'
    const URL_LABELS = 'World_Boundaries_and_Places'
    const URL_ROADS = 'World_Transportation'

    /**
     * Aplica as preferências de camadas após o mapa estar pronto.
     * Remove do mapa as overlays e a base layer que não devem estar ativas,
     * pois o vue2-leaflet adiciona todas as l-tile-layer ao mapa na montagem.
     */
    const applyMapLayerPreferences = () => {
        const map = mapInstance.value
        if (!map) return

        const layersToRemove = []
        map.eachLayer((layer) => {
            const url = layer._url || (layer.options && layer.options.url) || ''
            if (!url) return

            // Overlay: localidades — remover se preferência é não exibir
            if (url.indexOf(URL_LABELS) !== -1 && !showLocalities.value) {
                layersToRemove.push(layer)
                return
            }
            // Overlay: ruas — remover se preferência é não exibir
            if (url.indexOf(URL_ROADS) !== -1 && !showRoads.value) {
                layersToRemove.push(layer)
                return
            }
            // Base: OSM — remover se preferência é satélite
            if (url.indexOf(URL_OSM) !== -1 && defaultLayer.value === 'satellite') {
                layersToRemove.push(layer)
                return
            }
            // Base: satélite — remover se preferência é mapa
            if (url.indexOf(URL_SATELLITE) !== -1 && defaultLayer.value === 'map') {
                layersToRemove.push(layer)
            }
        })

        layersToRemove.forEach((layer) => {
            map.removeLayer(layer)
        })
    }

    const getMapBounds = () => {
        if (!mapInstance.value) return null

        const bounds = mapInstance.value.getBounds()
        return {
            northLat: bounds.getNorth(),
            southLat: bounds.getSouth(),
            eastLng: bounds.getEast(),
            westLng: bounds.getWest(),
            zoom: mapInstance.value.getZoom()
        }
    }

    const loadManufacturers = () => {
        store.dispatch('app-asset/fetchAssetsManufacture', 1)
            .then(response => {
                manufacturerOptions.value = [
                    {label: i18n.t('All'), value: ''},
                    ...response.data.data.map(v => ({
                        label: v.name,
                        value: v.id
                    }))
                ]
            })
            .catch(() => {})
    }

    // ---------- Cercas virtuais ----------

    const clearFences = () => {
        circleFences.value = []
        polygonFences.value = []
    }

    const calculateAveragePoint = (coordinates) => {
        if (!Array.isArray(coordinates) || coordinates.length === 0) {
            return null
        }

        let sumLat = 0
        let sumLng = 0

        coordinates.forEach(coord => {
            sumLat += coord.lat
            sumLng += coord.lng
        })

        const avgLat = sumLat / coordinates.length
        const avgLng = sumLng / coordinates.length

        return [avgLat, avgLng]
    }

    const fetchFenceDetail = async (id) => {
        try {
            const response = await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/fetchFence`, {id})
            return response.data || null
        } catch (e) {
            return null
        }
    }

    const loadFencesForMap = async () => {
        if (!showFencesOnMap.value) {
            clearFences()
            return
        }
        const seq = ++fenceLoadSeq
        fencesLoading.value = true
        clearFences()

        let currentPage = 1
        const perPage = 100
        let hasMore = true
        const fencesBuffer = []

        try {
            while (hasMore) {
                if (seq !== fenceLoadSeq || !showFencesOnMap.value) {
                    return
                }
                const response = await store.dispatch(`${FENCE_APP_STORE_MODULE_NAME}/fetchFences`, {
                    q: '',
                    customerId: customerId.value || '',
                    page: currentPage,
                    perPage
                })

                const pageData = response?.data?.data || []

                if (!pageData.length) {
                    hasMore = false
                } else {
                    fencesBuffer.push(...pageData)
                    currentPage += 1
                }
            }

            const circles = []
            const polygons = []
            for (let i = 0; i < fencesBuffer.length; i += FENCE_DETAIL_BATCH_SIZE) {
                if (seq !== fenceLoadSeq || !showFencesOnMap.value) {
                    return
                }
                const slice = fencesBuffer.slice(i, i + FENCE_DETAIL_BATCH_SIZE)
                // eslint-disable-next-line no-await-in-loop
                const details = await Promise.all(slice.map(f => fetchFenceDetail(f.id)))
                for (const fence of details) {
                    if (!fence) continue
                    if (fence.virtualFenceGeometryId === 1) circles.push(fence)
                    else if (fence.virtualFenceGeometryId === 2) polygons.push(fence)
                }
            }
            if (seq !== fenceLoadSeq || !showFencesOnMap.value) {
                return
            }
            circleFences.value = circles
            polygonFences.value = polygons
        } catch (_) {} finally {
            if (seq === fenceLoadSeq) {
                fencesLoading.value = false
            }
        }
    }

    const loadAssetTypes = () => {
        store.dispatch('app-asset/fetchAssetsType')
            .then(response => {
                assetTypeOptions.value = [
                    {label: i18n.t('All'), value: ''},
                    ...response.data.data.map(v => ({
                        label: v.name,
                        value: v.id
                    }))
                ]
            })
            .catch(() => {})
    }

    const loadModels = (manufacturerId) => {
        if (!manufacturerId) {
            modelOptions.value = [{label: i18n.t('All'), value: ''}]
            assetModelId.value = ''
            return
        }

        store.dispatch('app-asset/fetchAssetsModel', {id: manufacturerId, page: 1})
            .then(response => {
                modelOptions.value = [
                    {label: i18n.t('All'), value: ''},
                    ...response.data.data.map(v => ({
                        label: v.name,
                        value: v.id
                    }))
                ]
            })
            .catch(error => {
                modelOptions.value = [{label: i18n.t('All'), value: ''}]
            })
    }

    const searchCustomers = (text) => {
        if (!text || text.length < 3) {
            filteredCustomerOptions.value = []
            return
        }

        store.dispatch('app-asset/fetchCustomers', {q: text})
            .then(response => {
                filteredCustomerOptions.value = [{
                    data: response.data.data || []
                }]
            })
            .catch(error => {
                filteredCustomerOptions.value = []
            })
    }

    const selectCustomer = (option) => {
        if (option && option.item) {
            searchOwner.value = option.item.name
            customerId.value = option.item.id
            filteredCustomerOptions.value = []
        }
    }

    const clearAllFilters = () => {
        customerId.value = ''
        assetTypeId.value = ''
        assetBrandId.value = ''
        assetModelId.value = ''
        searchOwner.value = ''
        search.value = ''
        filteredCustomerOptions.value = []
        modelOptions.value = [{label: i18n.t('All'), value: ''}]
        initializeAssets()
    }

    const loadAssetsByViewPort = async () => {
        if (!mapInstance.value || !useViewPortMode.value) {
            return
        }

        const bounds = getMapBounds()
        if (!bounds) {
            return
        }
        loading.value = true
        error.value = null

        try {
            const response = await store.dispatch('app-asset/fetchAssetViewPort', {
                search: search.value,
                northLat: bounds.northLat,
                southLat: bounds.southLat,
                eastLng: bounds.eastLng,
                westLng: bounds.westLng,
                zoom: bounds.zoom,
                customerId: customerId.value || '',
                assetTypeId: assetTypeId.value || '',
                assetBrandId: assetBrandId.value || '',
                assetModelId: assetModelId.value || ''
            })

            const viewPortData = response.data.data || response.data || []

            const newAssets = []
            const newClusters = []

            viewPortData.forEach((item) => {

                if (item.kind === 'cluster') {
                    const lat = item.lastLat
                    const lng = item.lastLng
                    const assetCount = item.assetCount || 0

                    if (lat != null && lng != null && lat !== 0 && lng !== 0 && assetCount > 0) {
                        const workStatus = (item.lastWorkstatus || item.lastWorkStatus || 'noconnection').toLowerCase()
                        const validStatuses = ['on', 'off', 'failure', 'noconnection', 'neverconnected', 'severe', 'iddle']
                        const normalizedStatus = validStatuses.includes(workStatus) ? workStatus : 'noconnection'

                        newClusters.push({
                            lat,
                            lng,
                            count: assetCount, 
                            lastWorkstatus: normalizedStatus 
                        })
                    }
                }
                else if (item.kind === 'asset') {
                    const lat = item.lastLat
                    const lng = item.lastLng

                    if (lat != null && lng != null && lat !== 0 && lng !== 0) {

                        const workStatus = (item.lastWorkstatus || item.lastWorkStatus || 'noconnection').toLowerCase()
                        const validStatuses = ['on', 'off', 'failure', 'noconnection', 'neverconnected', 'severe', 'iddle']
                        const normalizedStatus = validStatuses.includes(workStatus) ? workStatus : 'noconnection'

                        const normalizedAsset = normalizeAssetIconFields({
                            ...item,
                            lastLat: lat,
                            lastLng: lng,
                            lastWorkstatus: normalizedStatus,
                            id: item.id || item.assetId,
                            assetId: item.assetId || item.id
                        })
                        newAssets.push(normalizedAsset)
                    }
                }
                else {
                    const lat = item.lastLat || item.lat || item.latitude
                    const lng = item.lastLng || item.lng || item.longitude

                    if (lat != null && lng != null && lat !== 0 && lng !== 0) {
                        const normalizedAsset = normalizeAssetIconFields({
                            ...item,
                            lastLat: lat,
                            lastLng: lng,
                            kind: 'asset'
                        })
                        newAssets.push(normalizedAsset)
                    }
                }
            })

            assets.value = newAssets
            clusters.value = newClusters

        } catch (err) {
            error.value = t('Error loading assets. Please try again.')
            assets.value = []
            clusters.value = []
        } finally {
            loading.value = false
        }
    }

    const loadAssetsByViewPortDebounced = debounce(loadAssetsByViewPort, DEBOUNCE_DELAY)


    const initializeAssets = async () => {
        loading.value = true
        error.value = null

        try {
            const response = await store.dispatch('app-asset/fetchAssets', {
                q: search.value,
                page: 1,
                perPage: 1, 
                sortBy: '',
                sortDesc: 'desc',
                customerId: customerId.value || '',
                assetTypeId: assetTypeId.value || '',
                assetBrandId: assetBrandId.value || '',
                assetModelId: assetModelId.value || '',
                workStatusList: []
            })

            totalAssets.value = response.data?.totalItems || response.totalItems || 0


            if (totalAssets.value > VIEWPORT_THRESHOLD) {
                useViewPortMode.value = true
            } else {
                useViewPortMode.value = false
                await loadAllAssets()
            }
        } catch (err) {
            error.value = t('Error loading assets. Please try again.')
            assets.value = []
            clusters.value = []
        } finally {
            loading.value = false
        }
    }

    const loadAllAssets = async () => {
        let allAssets = []
        let page = 1
        const perPage = 100
        let hasMore = true

        while (hasMore) {
            const response = await store.dispatch('app-asset/fetchAssets', {
                q: search.value,
                page,
                perPage,
                sortBy: '',
                sortDesc: 'desc',
                customerId: customerId.value || '',
                assetTypeId: assetTypeId.value || '',
                assetBrandId: assetBrandId.value || '',
                assetModelId: assetModelId.value || '',
                workStatusList: []
            })

            const pageAssets = response.data.data || []

            if (!pageAssets || pageAssets.length === 0) {
                hasMore = false
            } else {
                allAssets = allAssets.concat(pageAssets)
                page++
            }
        }

        const validAssets = allAssets
            .filter(asset => {
                const lat = asset.lastLat
                const lng = asset.lastLng
                return lat != null && lng != null && lat !== 0 && lng !== 0
            })
            .map(normalizeAssetIconFields)

        assets.value = validAssets
        clusters.value = []

        if (validAssets.length > 0) {
            adjustMapBounds()
        }
    }

    const adjustMapBounds = () => {
        if (useViewPortMode.value) return
        if (!mapInstance.value) return

        nextTick(() => {
            const validAssets = assetsWithValidCoordinates.value
            if (validAssets.length === 0) return

            if (validAssets.length === 1) {
                mapInstance.value.setView(
                    [validAssets[0].lastLat, validAssets[0].lastLng],
                    14
                )
                return
            }

            const bounds = validAssets.map(asset => [asset.lastLat, asset.lastLng])
            mapInstance.value.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })
        })
    }

    const onMapReady = (mapComponent) => {

        nextTick(() => {
            if (mapComponent && mapComponent.mapObject) {

                mapInstance.value = mapComponent.mapObject
                mapReady.value = true

                // Sincronizar preferências quando o usuário alterar camadas pelo controle do mapa
                mapComponent.mapObject.on('baselayerchange', (e) => {
                    const layer = resolveBaseLayer(e.name)
                    if (layer) defaultLayer.value = layer
                })
                mapComponent.mapObject.on('overlayadd', (e) => {
                    if (isLabelsOverlay(e.name)) showLocalities.value = true
                    else if (isRoadsOverlay(e.name)) showRoads.value = true
                })
                mapComponent.mapObject.on('overlayremove', (e) => {
                    if (isLabelsOverlay(e.name)) showLocalities.value = false
                    else if (isRoadsOverlay(e.name)) showRoads.value = false
                })

                // Aplicar preferências de camadas após o vue2-leaflet ter montado todas as l-tile-layer
                nextTick(() => {
                    setTimeout(applyMapLayerPreferences, 150)
                })

                mapComponent.mapObject.on('moveend', () => {
                    if (isFittingBounds.value) return
                    
                    if (useViewPortMode.value) {
                        loadAssetsByViewPortDebounced()
                    }
                })

                mapComponent.mapObject.on('zoomend', () => {
                    if (isFittingBounds.value) return
                    if (useViewPortMode.value) {
                        loadAssetsByViewPortDebounced()
                    }
                })

                if (useViewPortMode.value && !viewPortInitialLoadDone.value) {

                    viewPortInitialLoadDone.value = true
                    fitMapToBoundingBox().then(() => loadAssetsByViewPort())
                } else if (!useViewPortMode.value && assetsWithValidCoordinates.value.length > 0) {
                    adjustMapBounds()
                }
            }
        })
    }

    watch(useViewPortMode, (newVal, oldVal) => {
        if (newVal && !oldVal) {
            viewPortInitialLoadDone.value = false
            if (mapInstance.value) {
                viewPortInitialLoadDone.value = true
                fitMapToBoundingBox().then(() => loadAssetsByViewPort())
            }
        }
    })

    
    const updateViewPort = () => {
        if (useViewPortMode.value && mapInstance.value) {
            viewPortInitialLoadDone.value = false
            fitMapToBoundingBox().then(() => loadAssetsByViewPort())
        } else if (!useViewPortMode.value) {
            viewPortInitialLoadDone.value = false
            initializeAssets()
        }
    }

    const debouncedUpdateViewPort = debounce(() => {
        updateViewPort()
    }, FILTER_WATCH_DEBOUNCE_MS)

    const debouncedLoadFencesForMap = debounce(() => {
        loadFencesForMap()
    }, FILTER_WATCH_DEBOUNCE_MS)

    watch(assetBrandId, (newVal) => {
        assetModelId.value = ''
        loadModels(newVal)
    })

    // Ativos: refetch ao mudar filtros (debounced). Cercas dependem só do cliente — ver watch abaixo.
    watch([customerId, assetTypeId, assetBrandId, assetModelId, search], () => {
        debouncedUpdateViewPort()
    }, {deep: true})

    watch(customerId, () => {
        if (showFencesOnMap.value) {
            debouncedLoadFencesForMap()
        }
    })

    watch(showFencesOnMap, (val) => {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(STORAGE_KEY_PREFIX + 'showFences', String(val))
        }
        if (!val) {
            fenceLoadSeq += 1
            fencesLoading.value = false
            clearFences()
            return
        }
        loadFencesForMap()
    })

    // Watch para buscar clientes quando o texto mudar
    watch(searchOwner, (newVal) => {
        if (!newVal) {
            customerId.value = ''
            filteredCustomerOptions.value = []
        } else {
            searchCustomers(newVal)
        }
    })

    onMounted(() => {
        loadManufacturers()
        loadAssetTypes()
        loadModels(assetBrandId.value)
        initializeAssets()
        if (showFencesOnMap.value) {
            loadFencesForMap()
        }
    })

    onUnmounted(() => {
        loadAssetsByViewPortDebounced.cancel()
        debouncedUpdateViewPort.cancel()
        debouncedLoadFencesForMap.cancel()
        if (mapInstance.value) {
            mapInstance.value.off('moveend')
            mapInstance.value.off('zoomend')
            mapInstance.value.off('baselayerchange')
            mapInstance.value.off('overlayadd')
            mapInstance.value.off('overlayremove')
        }
        if (store.hasModule(ASSET_APP_STORE_MODULE_NAME)) {
            store.unregisterModule(ASSET_APP_STORE_MODULE_NAME)
        }
        if (store.hasModule(FENCE_APP_STORE_MODULE_NAME)) {
            store.unregisterModule(FENCE_APP_STORE_MODULE_NAME)
        }
    })

    /** Normaliza campos de ícone e tipo do ativo (API pode retornar camelCase, PascalCase ou nomes diferentes). */
    const normalizeAssetIconFields = (asset) => {
        if (!asset) return asset
        const iconUrl = asset.iconUrl || asset.IconUrl || asset.modelIconUrl || asset.ModelIconUrl
            || asset.assetModelIconUrl || asset.AssetModelIconUrl || asset.assetIconUrl || asset.icon || asset.Icon
        const assetType = asset.assetType ?? asset.AssetType ?? asset.assetTypeId ?? asset.AssetTypeId
        return { ...asset, iconUrl, assetType }
    }

    const getAssetIcon = (asset) => {
        if (!asset) return resolveIcon(null, null)
        const a = normalizeAssetIconFields(asset)
        const rawUrl = a.iconUrl
        const baseUrl = (axios && axios.defaults && axios.defaults.baseURL) || ''
        const iconUrl = rawUrl && String(rawUrl).trim() ? toAbsoluteIconUrl(String(rawUrl).trim(), baseUrl) : null
        const assetType = a.assetType != null ? a.assetType : null
        return resolveIcon(assetType, iconUrl)
    }

    const getDefaultAssetIcon = (asset) => {
        const a = asset ? normalizeAssetIconFields(asset) : null
        return resolveIcon(a?.assetType ?? a?.assetTypeId, null)
    }

    const getAssetPosition = (asset) => {
        return latLng(asset.lastLat, asset.lastLng)
    }

    const getClusterPosition = (cluster) => {
        return latLng(cluster.lat, cluster.lng)
    }

    const normalizeWorkStatus = (status) => {
        if (!status) return 'noconnection'
        const statusLower = (status || '').toLowerCase()
        const validStatuses = ['on', 'off', 'failure', 'noconnection', 'neverconnected', 'severe', 'iddle']
        return validStatuses.includes(statusLower) ? statusLower : 'noconnection'
    }

    const isValidCoordinate = (coord) => {
        return coord != null && coord !== 0 && !isNaN(coord)
    }

    const updateAssetFromTlm = (tlmData) => {
        if (!tlmData || !tlmData.assetId) {
            return {updated: false, found: false}
        }

        const assetId = tlmData.assetId
        const assetIndex = assets.value.findIndex(asset =>
            (asset.assetId && asset.assetId.toString() === assetId.toString()) ||
            (asset.id && asset.id.toString() === assetId.toString())
        )

        if (assetIndex === -1) {
            return {updated: false, found: false}
        }

        const asset = assets.value[assetIndex]
        let updated = false

        if (tlmData.workStatus !== undefined && tlmData.workStatus !== null) {
            const normalizedStatus = normalizeWorkStatus(tlmData.workStatus)
            if (asset.lastWorkstatus !== normalizedStatus) {
                asset.lastWorkstatus = normalizedStatus
                updated = true
            }
        }

        const lat = tlmData.lat
        const lng = tlmData.lng

        if (isValidCoordinate(lat) && isValidCoordinate(lng)) {
            const newLat = parseFloat(lat)
            const newLng = parseFloat(lng)

            const latChanged = asset.lastLat !== newLat
            const lngChanged = asset.lastLng !== newLng

            if (latChanged || lngChanged) {
                asset.lastLat = newLat
                asset.lastLng = newLng
                updated = true
            }
        }

        if (updated) {
            assets.value = [...assets.value]
        }

        return {updated, found: true}
    }

    const fetchAndDisplayAssetFromTlm = async (tlmData) => {
        if (!tlmData || !tlmData.assetId) return false

        const assetId = tlmData.assetId
        const now = Date.now()
        const lastAt = lastTlmDisplayFetchAt.value[assetId]
        if (lastAt != null && now - lastAt < TLM_DISPLAY_MIN_INTERVAL_MS) {
            return false
        }
        lastTlmDisplayFetchAt.value = {...lastTlmDisplayFetchAt.value, [assetId]: now}

        const normalizedStatus = normalizeWorkStatus(tlmData.workStatus)

        try {
            const summaryPromise = store.dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAssetSummary`, {
                id: assetId
            })
            const assetPromise = store
                .dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAsset`, {id: assetId})
                .catch(() => null)

            const [summaryResponse, assetResponse] = await Promise.all([summaryPromise, assetPromise])

            const summaryData = summaryResponse?.data
            if (!summaryData) return false

            const lat = summaryData.lastLat ?? summaryData.lat ?? summaryData.latitude
            const lng = summaryData.lastLng ?? summaryData.lng ?? summaryData.longitude

            if (!isValidCoordinate(lat) || !isValidCoordinate(lng)) {
                return false
            }

            const fullAsset = assetResponse?.data ?? null

            const newAsset = {
                ...(fullAsset || {}),
                ...summaryData,
                id: (fullAsset?.id ?? summaryData?.id ?? summaryData?.assetId ?? assetId)?.toString(),
                assetId: (fullAsset?.assetId ?? fullAsset?.id ?? summaryData?.assetId ?? summaryData?.id ?? assetId)?.toString(),
                lastLat: parseFloat(lat),
                lastLng: parseFloat(lng),
                lastWorkstatus: normalizedStatus,
                kind: 'asset'
            }

            // Evitar duplicata
            const alreadyExists = assets.value.some(
                a =>
                    (a.assetId && a.assetId.toString() === assetId.toString()) ||
                    (a.id && a.id.toString() === assetId.toString())
            )
            if (alreadyExists) return false

            assets.value = [...assets.value, newAsset]
            return true
        } catch (err) {
            return false
        }
    }

    const fitMapToBoundingBox = () => {
        return new Promise(async (resolve) => {
            if (!mapInstance.value) return resolve()

            try {
                const response = await store.dispatch('app-asset/fetchAssetsBoundingBox', {
                    customerId: customerId.value || '',
                    assetTypeId: assetTypeId.value || '',
                    assetBrandId: assetBrandId.value || '',
                    assetModelId: assetModelId.value || '',
                    search: search.value
                })

                const bbox = response.data
                if (!bbox || bbox.total === 0) return resolve()

                if (bbox.total === 1) {
                    mapInstance.value.setView([bbox.minLat, bbox.minLng], 14)
                    // setView também é assíncrono, aguardar moveend
                    mapInstance.value.once('moveend', () => resolve())
                    return
                }

                const bounds = [
                    [bbox.minLat, bbox.minLng],
                    [bbox.maxLat, bbox.maxLng]
                ]

                isFittingBounds.value = true
                let resolved = false

                mapInstance.value.once('moveend', () => {
                    if (resolved) return
                    resolved = true
                    isFittingBounds.value = false
                    resolve()
                })

                setTimeout(() => {
                    if (resolved) return
                    resolved = true
                    isFittingBounds.value = false
                    resolve()
                }, 3000)

                mapInstance.value.fitBounds(bounds, { padding: [25, 25], maxZoom: 14 })

            } catch (err) {
                resolve() // sempre resolver para não travar o .then()
            }
        })
    }

    return {
        // Estado do mapa
        mapReady,
        center,
        zoom,
        mapOptions,
        defaultLayer,
        showLocalities,
        showRoads,
        arcgisSatelliteUrl,
        arcgisLabelsUrl,
        arcgisRoadsUrl,
        onMapReady,
        mapRef,
        mapInstance,

        // Estado dos ativos
        assets,
        clusters,
        assetsWithValidCoordinates,
        loading,
        error,
        totalAssets,
        useViewPortMode,

        // Cercas virtuais
        circleFences,
        polygonFences,
        showFencesOnMap,
        fencesLoading,

        // Filtros
        customerId,
        assetTypeId,
        assetBrandId,
        assetModelId,
        showFilters,
        searchOwner,
        hasActiveFilters,
        viewMode,
        search,

        // Opções dos filtros
        manufacturerOptions,
        assetTypeOptions,
        modelOptions,
        filteredCustomerOptions,

        // Funções
        initializeAssets,
        clearAllFilters,
        selectCustomer,
        getAssetIcon,
        getDefaultAssetIcon,
        getAssetPosition,
        getClusterPosition,
        adjustMapBounds,
        loadAssetsByViewPort,
        updateAssetFromTlm,
        fetchAndDisplayAssetFromTlm
    }
}
